"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {
   composeLiveDictationValue,
   DICTATION_MAX_SESSION_MS,
   isDictationSupported,
   isMicrophonePermissionDenied,
   mergeDictationTranscript,
   requestDictationSession,
   subscribeDictationVisibilityStop,
   type DictationToken,
} from "@/utils/chat/dictation";
import {
   startSonioxDictation,
   type SonioxDictationHandles,
} from "@/utils/chat/startSonioxDictation";

export type GailGptDictationStatus = "unsupported" | "idle" | "recording" | "transcribing";

export type DictationStopReason = "user" | "max_duration" | "tab_hidden";

export type DictationRecorderError = {
   reason: string;
   status?: number;
   durationMs?: number;
   producedText?: boolean;
};

type UseDictationRecorderProps = {
   disabled?: boolean;
   onStarted?: () => void;
   onError: (error: DictationRecorderError) => void;
   onAutoStop: (reason: Exclude<DictationStopReason, "user">) => void;
   onLiveTranscript: (text: string) => void;
   onTranscript: (text: string, meta: {durationMs: number; stopReason: DictationStopReason}) => void;
};

/**
 * Soniox direct-stream session for click-to-record dictation.
 * Live tokens update the composer during recording; stop waits for finals.
 */
export function useDictationRecorder({
   disabled = false,
   onStarted,
   onError,
   onAutoStop,
   onLiveTranscript,
   onTranscript,
}: UseDictationRecorderProps) {
   const [status, setStatus] = useState<GailGptDictationStatus>("unsupported");
   const [isSupported, setIsSupported] = useState(false);

   const statusRef = useRef<GailGptDictationStatus>("unsupported");
   const onStartedRef = useRef(onStarted);
   const onErrorRef = useRef(onError);
   const onAutoStopRef = useRef(onAutoStop);
   const onLiveTranscriptRef = useRef(onLiveTranscript);
   const onTranscriptRef = useRef(onTranscript);
   const clientRef = useRef<SonioxDictationHandles | null>(null);
   const mediaStreamRef = useRef<MediaStream | null>(null);
   const abortControllerRef = useRef<AbortController | null>(null);
   const sessionIdRef = useRef(0);
   const sessionActiveRef = useRef(false);
   const sessionStartedAtRef = useRef<number | null>(null);
   const stopReasonRef = useRef<DictationStopReason>("user");
   const committedTextRef = useRef("");
   const nonFinalTextRef = useRef("");
   const maxSessionTimerRef = useRef<number | null>(null);
   const disposedRef = useRef(false);
   const stopRecordingRef = useRef<(reason?: DictationStopReason) => void>(() => {});

   onStartedRef.current = onStarted;
   onErrorRef.current = onError;
   onAutoStopRef.current = onAutoStop;
   onLiveTranscriptRef.current = onLiveTranscript;
   onTranscriptRef.current = onTranscript;

   const updateStatus = useCallback((next: GailGptDictationStatus) => {
      statusRef.current = next;
      setStatus(next);
   }, []);

   useEffect(() => {
      disposedRef.current = false;
      const supported = isDictationSupported();
      setIsSupported(supported);
      updateStatus(supported ? "idle" : "unsupported");
      return () => {
         disposedRef.current = true;
      };
   }, [updateStatus]);

   const clearMaxSessionTimer = useCallback(() => {
      if (maxSessionTimerRef.current !== null) {
         window.clearTimeout(maxSessionTimerRef.current);
         maxSessionTimerRef.current = null;
      }
   }, []);

   const stopTracks = useCallback(() => {
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
   }, []);

   const releaseHardware = useCallback(() => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      clientRef.current?.cancel();
      clientRef.current = null;
      stopTracks();
   }, [stopTracks]);

   const teardownSession = useCallback(() => {
      sessionIdRef.current += 1;
      sessionActiveRef.current = false;
      committedTextRef.current = "";
      nonFinalTextRef.current = "";
      clearMaxSessionTimer();
      releaseHardware();
   }, [clearMaxSessionTimer, releaseHardware]);

   const sessionDurationMs = useCallback(() => {
      return sessionStartedAtRef.current ? Date.now() - sessionStartedAtRef.current : 0;
   }, []);

   const failSession = useCallback(
      (error: DictationRecorderError) => {
         onErrorRef.current({
            ...error,
            durationMs: error.durationMs ?? sessionDurationMs(),
            producedText: error.producedText ?? committedTextRef.current.trim().length > 0,
         });
      },
      [sessionDurationMs]
   );

   const publishLiveTranscript = useCallback((nonFinalText: string) => {
      onLiveTranscriptRef.current(
         composeLiveDictationValue(committedTextRef.current, nonFinalText).trimStart()
      );
   }, []);

   const handlePartialTokens = useCallback(
      (tokens: DictationToken[]) => {
         const {committedText, nonFinalText} = mergeDictationTranscript(
            committedTextRef.current,
            tokens
         );
         committedTextRef.current = committedText;
         nonFinalTextRef.current = nonFinalText;
         publishLiveTranscript(nonFinalText);
      },
      [publishLiveTranscript]
   );

   const finishSession = useCallback(
      (sessionId: number) => {
         if (disposedRef.current || sessionId !== sessionIdRef.current) {
            return;
         }
         clientRef.current = null;
         stopTracks();
         const text = (committedTextRef.current || nonFinalTextRef.current).trim();
         const durationMs = sessionDurationMs();
         const stopReason = stopReasonRef.current;
         sessionActiveRef.current = false;
         clearMaxSessionTimer();
         if (!text) {
            failSession({reason: "empty_transcript", producedText: false, durationMs});
            updateStatus("idle");
            return;
         }
         onTranscriptRef.current(text, {durationMs, stopReason});
         updateStatus("idle");
      },
      [clearMaxSessionTimer, failSession, sessionDurationMs, stopTracks, updateStatus]
   );

   const start = useCallback(async () => {
      if (disabled || statusRef.current !== "idle" || !isSupported) {
         return;
      }

      try {
         const stream = await navigator.mediaDevices.getUserMedia({audio: true});
         if (disposedRef.current) {
            stream.getTracks().forEach((track) => track.stop());
            return;
         }

         const sessionId = sessionIdRef.current + 1;
         sessionIdRef.current = sessionId;
         mediaStreamRef.current = stream;
         sessionActiveRef.current = true;
         sessionStartedAtRef.current = Date.now();
         stopReasonRef.current = "user";
         committedTextRef.current = "";
         nonFinalTextRef.current = "";
         clearMaxSessionTimer();
         maxSessionTimerRef.current = window.setTimeout(() => {
            maxSessionTimerRef.current = null;
            stopRecordingRef.current("max_duration");
         }, DICTATION_MAX_SESSION_MS);

         const controller = new AbortController();
         abortControllerRef.current = controller;
         updateStatus("recording");
         onStartedRef.current?.();

         const result = await requestDictationSession(controller.signal);
         if (result.ok === false && result.aborted) {
            return;
         }
         if (disposedRef.current || sessionId !== sessionIdRef.current) {
            return;
         }
         if (!result.ok) {
            failSession({
               reason: result.code ?? `http_${result.status}`,
               status: result.status,
            });
            teardownSession();
            updateStatus("idle");
            return;
         }

         stopTracks();
         const handles = await startSonioxDictation({
            session: result.session,
            onPartialResult: (tokens) => {
               if (disposedRef.current || sessionId !== sessionIdRef.current) {
                  return;
               }
               handlePartialTokens(tokens);
            },
            onFinished: () => {
               finishSession(sessionId);
            },
            onError: (sonioxStatus) => {
               if (disposedRef.current || sessionId !== sessionIdRef.current) {
                  return;
               }
               failSession({
                  reason: sonioxStatus === "get_user_media_failed" ? "mic_denied" : sonioxStatus,
               });
               teardownSession();
               updateStatus("idle");
            },
         });
         if (disposedRef.current || sessionId !== sessionIdRef.current) {
            handles.cancel();
            return;
         }
         clientRef.current = handles;
      } catch (error) {
         failSession({
            reason: isMicrophonePermissionDenied(error)
               ? "mic_denied"
               : sessionActiveRef.current
                 ? "network_error"
                 : "mic_error",
         });
         if (sessionActiveRef.current) {
            teardownSession();
         }
         updateStatus("idle");
      }
   }, [
      clearMaxSessionTimer,
      disabled,
      failSession,
      finishSession,
      handlePartialTokens,
      isSupported,
      stopTracks,
      teardownSession,
      updateStatus,
   ]);

   const stop = useCallback(
      (reason: DictationStopReason = "user") => {
         if (!sessionActiveRef.current) {
            return;
         }
         stopReasonRef.current = reason;
         sessionActiveRef.current = false;
         clearMaxSessionTimer();
         if (reason !== "user") {
            onAutoStopRef.current(reason);
         }
         const client = clientRef.current;
         if (client) {
            updateStatus("transcribing");
            client.stop();
            return;
         }
         teardownSession();
         updateStatus("idle");
      },
      [clearMaxSessionTimer, teardownSession, updateStatus]
   );

   stopRecordingRef.current = stop;

   const toggle = useCallback(() => {
      if (disabled) {
         return;
      }
      if (statusRef.current === "recording") {
         stop();
         return;
      }
      if (statusRef.current === "idle") {
         void start();
      }
   }, [disabled, start, stop]);

   const cancel = useCallback(() => {
      if (statusRef.current !== "recording" && statusRef.current !== "transcribing") {
         return;
      }
      teardownSession();
      updateStatus("idle");
   }, [teardownSession, updateStatus]);

   useEffect(() => subscribeDictationVisibilityStop(() => stopRecordingRef.current("tab_hidden")), []);

   useEffect(() => {
      return () => {
         disposedRef.current = true;
         sessionActiveRef.current = false;
         clearMaxSessionTimer();
         releaseHardware();
      };
   }, [clearMaxSessionTimer, releaseHardware]);

   return {
      status,
      isSupported,
      start,
      stop,
      toggle,
      cancel,
   };
}
